import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatColumnDef, MatCellDef, MatCell, MatRowDef, MatRow, MatNoDataRow } from '@angular/material/table';
import { Cart, CartActions, CartLineSources, CartSelectors, DeliveryInformation, LocationSilo, Silo, SiloDistributionTypes } from '@next/core-lib/cart';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { AgrFormModule } from '@next/core-lib/form';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableV2Module } from '@next/core-lib/table-v2';
import { Store } from '@ngrx/store';
import { map, Observable, Subject, takeUntil } from 'rxjs';

import { decorateFormControl } from 'src/app/product/helpers/add-to-cart-helpers';
import { FormatFlexibilityDatePipe } from 'src/app/product/pipes/format-flexibility-date.pipe';
import { FormatSiloTitlePipe } from 'src/app/product/pipes/format-silo-title.pipe';
import * as ProductActions from 'src/app/product/store/actions';
import { selectExpandedSiloIds, selectSiloWithProductListById } from 'src/app/product/store/selectors';
import * as ProductSelectors from 'src/app/product/store/selectors';
import { deleteSiloProductListItemRequest, openSiloProductSupplementsDialog } from 'src/app/store/actions';

import { SiloProductListDataSource } from './silo-product-list.datasource';

import { SiloProductItem, SiloProductItemViewModel } from '../../models/silo-product-list.models';
import { SiloProductListDeliveryDateDialogComponent } from '../silo-product-list-delivery-date-dialog/silo-product-list-delivery-date-dialog.component';

@Component({
  selector: 'app-silos-product-list',
  templateUrl: './silo-product-list.component.html',
  styleUrls: ['./silo-product-list.component.scss'],
  imports: [
    TableV2Module,
    MatColumnDef,
    MatCellDef,
    MatCell,
    NgIf,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatInput,
    AgrFormModule,
    FormsModule,
    ReactiveFormsModule,
    MatSuffix,
    MatSelect,
    MatOption,
    NgFor,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    AsyncPipe,
    AgrI18nSharedModule,
    FormatFlexibilityDatePipe,
    FormatSiloTitlePipe,
  ],
})
export class SiloProductListComponent implements OnInit, OnDestroy {
  @Input() siloId: number;
  public dataSource: SiloProductListDataSource;
  public readonly isExpanded$ = this.store.select(selectExpandedSiloIds).pipe(map((ids) => ids.includes(this.siloId)));
  public tableColumns: string[] = ['deleteFromList', 'name', 'quantity', 'remainderSilo', 'supplements', 'deliveryInformation', 'lineActions'];
  public quantityForms = new FormArray([]);
  public deliveryInformationForms = new FormArray<FormControl<DeliveryInformation>>([]);
  public remainderSiloForms = new FormArray<FormControl<LocationSilo>>([]);
  public readonly availableRestSilos$: Observable<LocationSilo[]> = this.store
    .select(ProductSelectors.selectSilosWithProductLists)
    .pipe(map((silos) => silos.filter((silo) => this.silo.id !== silo?.id)));

  private readonly destroy$ = new Subject<void>();
  private cart: Cart;
  private silo: LocationSilo;

  constructor(
    private readonly store: Store,
    private readonly localeService: LocaleService,
    private readonly dialogService: DialogService,
    @Inject(BreakpointServiceInjector) readonly breakpointService: BreakpointService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.breakpointService.ltSm$.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      if (isMobile) {
        this.tableColumns.shift();
      }
    });

    this.dataSource = new SiloProductListDataSource(this.store);
    this.dataSource.connect().subscribe((products) => {
      this.quantityForms.clear();
      this.deliveryInformationForms.clear();
      this.remainderSiloForms.clear();

      products.forEach((product) => {
        const formControl = new FormControl(product.quantity);
        this.quantityForms.push(formControl);
        decorateFormControl(product, formControl, this.localeService);

        const deliveryDateControl = new FormControl<DeliveryInformation>(product.deliveryInformation);
        this.deliveryInformationForms.push(deliveryDateControl);

        this.remainderSiloForms.push(new FormControl<LocationSilo>(null));
      });
    });
    this.store.select(CartSelectors.selectCurrentSalesOrderCart).subscribe((cart) => {
      this.cart = cart;
      this.dataSource.setCartType(cart?.type);
    });
    this.store.select(selectSiloWithProductListById(this.siloId)).subscribe((silo) => {
      this.silo = silo;
      this.dataSource.setSilo(silo);
    });
  }

  addToCart(selectedItem: SiloProductItem, itemIndex: number): void {
    const quantity = this.quantityForms.at(itemIndex).value;
    const deliveryInformation = this.deliveryInformationForms.at(itemIndex).value;
    const remainderSilo = this.remainderSiloForms.at(itemIndex).value;

    const silos: Silo[] = [];
    if (remainderSilo) {
      silos.push({ id: this.silo.id, siloNumber: this.silo.siloNumber, distributionType: SiloDistributionTypes.full } as Silo);
      silos.push({ id: remainderSilo.id, siloNumber: remainderSilo.siloNumber, distributionType: SiloDistributionTypes.leftover } as Silo);
    } else {
      silos.push({ id: this.silo.id, siloNumber: this.silo.siloNumber, distributionType: SiloDistributionTypes.exact, quantity } as Silo);
    }

    this.store.dispatch(
      CartActions.addCartLineToCart({
        cartLine: {
          skuId: selectedItem.id,
          quantity,
          deliveryInformation,
          productSupplements: selectedItem.supplements,
          silos,
          source: CartLineSources.SILO_PRODUCT_LIST,
        },
        cartId: this.cart?.id,
      }),
    );
  }

  deleteSiloProductListItem(selectedItem: SiloProductItem) {
    this.store.dispatch(deleteSiloProductListItemRequest({ siloId: this.siloId, siloProductItem: selectedItem }));
  }

  openProductSupplementDialog(siloId: number, siloProduct: SiloProductItem) {
    this.store.dispatch(ProductActions.setSelectedProductSiloLine({ selectedProductSiloLine: siloProduct }));
    this.store.dispatch(ProductActions.getVariantsRequest());
    this.store.dispatch(openSiloProductSupplementsDialog({ siloId, siloProduct }));
  }

  openDeliveryDateDialog(productListItem: SiloProductItemViewModel, formControl: FormControl<DeliveryInformation>) {
    const subscription = this.dialogService
      .fullscreenDialog(SiloProductListDeliveryDateDialogComponent, {
        productListItem,
        deliveryInformation: formControl.value,
      })
      .subscribe(({ status, result }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          formControl.patchValue(result.deliveryInformation);
        }

        subscription.unsubscribe();
      });
  }
}
